import React, { useState } from 'react';
import {
  Box,
  Typography,
  Rating,
  TextField,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Autocomplete,
  Stack,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  CreateReviewCommand,
  ReviewType,
} from '../../configuration/api/apiSchema';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

// Common tags for reviews
const COMMON_TAGS = [
  'Professional',
  'Punctual',
  'Friendly',
  'Skilled',
  'Responsive',
  'Reliable',
  'Efficient',
  'Knowledgeable',
  'Affordable',
  'Clean',
  'Thorough',
  'Communicative',
  'Respectful',
  'Honest',
  'Experienced',
];

interface CreateReviewFormProps {
  orderId: string;
  reviewerId: string;
  revieweeId: string;
  reviewType: ReviewType;
  onSubmit: (data: CreateReviewCommand) => Promise<void>;
  onCancel?: () => void;
}

const CreateReviewForm: React.FC<CreateReviewFormProps> = ({
  orderId,
  reviewerId,
  revieweeId,
  reviewType,
  onSubmit,
  onCancel,
}) => {
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isFormValid = rating !== null && rating > 0 && comment !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) {
      setError('Please provide a rating');
      return;
    }

    const reviewData: CreateReviewCommand = {
      orderId,
      reviewerId,
      revieweeId,
      rating,
      comment: comment.trim() || null,
      tags: selectedTags.length > 0 ? selectedTags : [],
      type: reviewType,
    };

    setLoading(true);
    setError(null);

    try {
      await onSubmit(reviewData);
      setSuccess(true);
      // Reset form
      setRating(null);
      setComment('');
      setSelectedTags([]);
    } catch (err: any) {
      setError(err.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledPaper elevation={1} sx={{ p: 3 }} onSubmit={handleSubmit}>
      <Stack spacing={3} component='form' sx={{ height: '100%' }}>
        {error && (
          <Alert severity='error' onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity='success' onClose={() => setSuccess(false)}>
            Review submitted successfully!
          </Alert>
        )}

        <Box>
          <Typography
            variant='subtitle1'
            gutterBottom
            color={!rating ? 'error' : 'inherit'}
          >
            Rating *
          </Typography>
          <Rating
            value={rating}
            onChange={(_, newValue) => setRating(newValue)}
            size='large'
            precision={1}
          />
        </Box>

        <TextField
          fullWidth
          variant='filled'
          multiline
          minRows={4}
          maxRows={6}
          label='Review Comment'
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder='Share your experience...'
          sx={{ flex: 1 }}
        />

        <Autocomplete
          multiple
          options={COMMON_TAGS}
          value={selectedTags}
          onChange={(_, newValue) => setSelectedTags(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Tags'
              placeholder='Select or type tags'
              helperText='Add tags to highlight key aspects of your experience'
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant='outlined'
                label={option}
                {...getTagProps({ index })}
                key={option}
              />
            ))
          }
          freeSolo
        />

        <Box display='flex' gap={2} justifyContent='flex-end'>
          {onCancel && (
            <Button
              onClick={onCancel}
              disabled={loading}
              variant='outlined'
              color='inherit'
            >
              Cancel
            </Button>
          )}
          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={loading || !isFormValid}
            sx={{
              minWidth: 100,
              '&.Mui-disabled': {
                backgroundColor: 'primary.main',
                color: 'white',
                opacity: 0.7,
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit Review'}
          </Button>
        </Box>
      </Stack>
    </StyledPaper>
  );
};

export default CreateReviewForm;
